import React, { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { Button, deleteIconButtonClass } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { productosAPI, type ProductoGrupo } from "@/lib/api";
import { toast } from "sonner";
import { formatRubrosApiError } from "./rubrosApiErrors";
import { ListNewLoading, ListToolbar } from "@/components/ListNewButton";

interface RubrosListadoProps {
  onEdit?: (row: ProductoGrupo) => void;
  onNew?: () => void;
  refreshTrigger?: number;
  includeInactive?: boolean;
  onListChanged?: () => void;
}

export function RubrosListado({
  onEdit,
  onNew,
  refreshTrigger = 0,
  includeInactive = false,
  onListChanged,
}: RubrosListadoProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [rows, setRows] = useState<ProductoGrupo[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState<ProductoGrupo | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const data = await productosAPI.listGrupos({ includeInactive });
        setRows(data);
      } catch (error) {
        console.error("Error loading rubros:", error);
        toast.error(formatRubrosApiError(error, "Error al cargar rubros"));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refreshTrigger, includeInactive]);

  const filtered = rows.filter(
    (r) =>
      !searchQuery ||
      r.codigo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.nombre?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await productosAPI.deleteGrupo(pendingDelete.codigo);
      toast.success(`Rubro eliminado (${pendingDelete.codigo})`);
      setPendingDelete(null);
      onListChanged?.();
    } catch (error) {
      console.error(error);
      toast.error(formatRubrosApiError(error, "No se pudo eliminar el rubro"));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <ListNewLoading onNew={onNew} message="Cargando rubros..." />;
  }

  return (
    <div className="space-y-4 mt-4">
      <ListToolbar onNew={onNew}>
        <label className="block text-sm font-medium mb-1">Buscar</label>
        <input
          type="text"
          placeholder="Código o nombre..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
        />
      </ListToolbar>

      {filtered.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">No hay rubros para mostrar</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-2 text-left">Código</th>
                <th className="px-4 py-2 text-left">Nombre</th>
                <th className="px-4 py-2 text-center">Activo</th>
                <th className="px-4 py-2 text-right w-14"> </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.codigo}
                  className="border-b hover:bg-muted/50 cursor-pointer"
                  onClick={() => onEdit?.(r)}
                >
                  <td className="px-4 py-2 font-mono">{r.codigo}</td>
                  <td className="px-4 py-2">{r.nombre}</td>
                  <td className="px-4 py-2 text-center">{r.activo ? "Sí" : "No"}</td>
                  <td className="px-2 py-1 text-right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className={`h-8 w-8 ${deleteIconButtonClass}`}
                      title="Eliminar rubro"
                      aria-label="Eliminar rubro"
                      onClick={() => setPendingDelete(r)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar rubro?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <span className="font-mono font-semibold">{pendingDelete?.codigo}</span> (
              {pendingDelete?.nombre}). También se borrarán los subrubros de ese rubro. En productos,
              las referencias a ese rubro o subrubros quedarán vacías. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
            >
              {deleting ? "Eliminando…" : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
